import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOrder } from '../../contexts/OrderContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Webcam from 'react-webcam';
import supabase from '../../lib/supabase';

const { FiCamera, FiUpload, FiCheckCircle, FiX, FiAlertCircle, FiFileText, FiArrowRight, FiHome, FiMonitor } = FiIcons;

const DocumentUpload = () => {
  const { currentOrder, updateOrder, addOrder } = useOrder();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  
  const [documents, setDocuments] = useState({});
  const [activeDocument, setActiveDocument] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [allUploaded, setAllUploaded] = useState(false);
  const [useBackCamera, setUseBackCamera] = useState(true);

  // Helper: upload a file/blob to Supabase Storage and return public URL
  const uploadToSupabase = async (docName, file) => {
    try {
      const orderId = currentOrder?.orderId || `H2A${Date.now()}`;
      const ext = (file.name && file.name.split('.').pop()) || 'jpg';
      const path = `${orderId}/${docName}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('customer_documents')
        .upload(path, file, { upsert: true, cacheControl: '3600' });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('customer_documents').getPublicUrl(path);
      return data.publicUrl;
    } catch (e) {
      console.error('Supabase upload error:', e);
      toast.error('Failed to upload to server');
      return null;
    }
  };

  // Helper: sync current documents map to Supabase tables for operator viewing
  const syncDocsToSupabase = async (docsMap) => {
    try {
      if (!currentOrder?.orderId) return;
      // Build minimal JSON: { docName: { url, name, type } }
      const payload = Object.entries(docsMap).reduce((acc, [key, val]) => {
        if (val?.url) acc[key] = { url: val.url, name: val.name || key, type: val.type || '' };
        return acc;
      }, {});
      // Update available orders (source)
      await supabase
        .from('available_orders_a7b5c9')
        .update({ documents: payload })
        .eq('order_id', currentOrder.orderId);
      // Also update operator orders if already assigned
      await supabase
        .from('operator_orders_a7b5c9')
        .update({ documents: payload })
        .eq('order_id', currentOrder.orderId);
    } catch (e) {
      console.error('Sync documents error:', e);
    }
  };
  
  // Redirect if no current order or if it's a home service
  useEffect(() => {
    console.log("DocumentUpload: currentOrder =", currentOrder);
    
    if (!currentOrder) {
      console.log("DocumentUpload: No current order, redirecting to services");
      toast.error('Please select a service first');
      navigate('/service-categories');
      return;
    }

    console.log("DocumentUpload: Processing order with data:", currentOrder);

    // Check if this is a home service (should skip document upload)
    if (currentOrder.serviceType === 'home') {
      console.log("DocumentUpload: Home service detected, redirecting to payment");
      toast.info('Home services skip document upload. Redirecting to payment...');
      navigate('/payment');
      return;
    }

    // Initialize document states for online services
    const initialDocs = {};
    const initialProgress = {};
    
    // FIX START: Check if currentOrder.documents is an array before using map
    const documentsList = Array.isArray(currentOrder.documents) 
      ? currentOrder.documents 
      : [
        'Photo',
        'Signature', 
        'SSC Certificate',
        'Aadhaar',
        'Caste Certificate'
      ];
    // FIX END
    
    documentsList.forEach(doc => {
      initialDocs[doc] = null;
      initialProgress[doc] = 0;
    });
    setDocuments(initialDocs);
    setUploadProgress(initialProgress);
  }, [currentOrder, navigate]);

  // Check if all documents are uploaded
  useEffect(() => {
    if (!currentOrder || currentOrder.serviceType === 'home') return;
    
    // FIX START: Check if currentOrder.documents is an array before using every()
    const allDone = Array.isArray(currentOrder.documents) 
      && currentOrder.documents.every(doc => documents[doc] && documents[doc].url);
    setAllUploaded(allDone);
    // FIX END
  }, [documents, currentOrder]);

  const handleFileSelect = async (e, documentName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }
    
    // Simulate upload progress
    setUploadProgress(prev => ({ ...prev, [documentName]: 10 }));

    // Upload to Supabase Storage
    const publicUrl = await uploadToSupabase(documentName, file);
    if (!publicUrl) {
      setUploadProgress(prev => ({ ...prev, [documentName]: 0 }));
      return;
    }

    // Update local state with URL
    const nextDocs = {
      ...documents,
      [documentName]: {
        name: file.name,
        type: file.type,
        size: file.size,
        url: publicUrl
      }
    };
    setDocuments(nextDocs);
    setUploadProgress(prev => ({ ...prev, [documentName]: 100 }));
    toast.success(`${documentName} uploaded successfully`);

    // Sync to Supabase rows so operator can view
    await syncDocsToSupabase(nextDocs);
  };

  const dataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const captureFromWebcam = async () => {
    if (!webcamRef.current) return;
    
    const imageSrc = webcamRef.current.getScreenshot();
    
    if (imageSrc) {
      const file = dataUrlToFile(imageSrc, `${activeDocument}.jpg`);
      setUploadProgress(prev => ({ ...prev, [activeDocument]: 10 }));
      const publicUrl = await uploadToSupabase(activeDocument, file);
      if (!publicUrl) {
        setUploadProgress(prev => ({ ...prev, [activeDocument]: 0 }));
        return;
      }
      const nextDocs = {
        ...documents,
        [activeDocument]: {
          name: `${activeDocument}.jpg`,
          type: 'image/jpeg',
          size: file.size,
          url: publicUrl
        }
      };
      setDocuments(nextDocs);
      setShowCamera(false);
      setUploadProgress(prev => ({ ...prev, [activeDocument]: 100 }));
      toast.success(`${activeDocument} captured successfully`);
      await syncDocsToSupabase(nextDocs);
    } else {
      toast.error('Failed to capture image. Please try again.');
    }
  };

  const removeDocument = async (documentName) => {
    const next = { ...documents, [documentName]: null };
    setDocuments(next);
    setUploadProgress(prev => ({ ...prev, [documentName]: 0 }));
    await syncDocsToSupabase(next);
  };

  const handleContinue = async () => {
    if (!allUploaded) {
      toast.error('Please upload all required documents');
      return;
    }
    
    // Update the current order with document information (URLs saved)
    const updatedOrder = {
      ...currentOrder,
      status: 'documents_uploaded',
      documents: documents,
      updatedAt: new Date().toISOString()
    };
    
    updateOrder(updatedOrder.id, updatedOrder);
    addOrder(updatedOrder);

    // Ensure Supabase rows are synced (guard)
    await syncDocsToSupabase(documents);
    
    navigate('/payment');
  };

  // Don't render if no order or if it's a home service
  if (!currentOrder || currentOrder.serviceType === 'home') return null;

  const documentTips = {
    'Photo': 'Upload a recent passport size photograph with white background',
    'Signature': 'Sign on a white paper and upload a clear image',
    'Aadhaar Card': 'Upload both sides of your Aadhaar card',
    'SSC Certificate': 'Upload your 10th class certificate',
    'Intermediate Certificate': 'Upload your 12th class certificate',
    'Resume': 'Upload in PDF format for better quality',
    'Degree Certificate': 'Upload your degree certificate with all marks',
    'Experience Certificate': 'Upload your work experience certificate',
    'Income Certificate': 'Upload your income certificate issued by authority',
    'Caste Certificate': 'Upload your caste certificate issued by authority',
    'Category Certificate': 'Upload your category certificate issued by authority',
    'Bank Details': 'Upload your bank passbook first page or cancelled cheque',
  };

  // FIX START: Check if currentOrder.documents is an array before rendering the map
  const documentsToRender = Array.isArray(currentOrder.documents) 
    ? currentOrder.documents 
    : [
      'Photo',
      'Signature', 
      'SSC Certificate',
      'Aadhaar',
      'Caste Certificate'
    ];
  // FIX END

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Service Type Banner */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
          className="mb-6"
        >
          <Card className="p-4 bg-green-50 border-green-200 border">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-lg">
                <SafeIcon icon={FiMonitor} className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Online Services</h2>
                <p className="text-sm text-gray-600">Document upload is required for online applications</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Upload Documents</h1>
          <p className="text-gray-600 mt-2">Please upload all the required documents for your {currentOrder.serviceType}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Document Upload Section */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Required Documents</h2>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        Please ensure that all documents are clear and legible. Maximum file size: 5MB per document.
                      </p>
                    </div>
                  </div>
                </div>

                {documentsToRender.map((doc, index) => (
                  <motion.div
                    key={doc}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiFileText} className="w-6 h-6 text-gray-500" />
                        <div>
                          <h3 className="font-medium text-gray-900">{doc}</h3>
                          <p className="text-xs text-gray-500">{documentTips[doc] || 'Upload a clear, legible copy'}</p>
                        </div>
                      </div>
                      
                      {documents[doc] ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-green-600 flex items-center">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-1" />
                            <span className="text-sm">Uploaded</span>
                          </span>
                          <button 
                            onClick={() => removeDocument(doc)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <SafeIcon icon={FiX} className="w-5 h-5" />
                          </button>
                        </div>
                      ) : uploadProgress[doc] > 0 && uploadProgress[doc] < 100 ? (
                        <div className="w-24">
                          <div className="bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full" 
                              style={{ width: `${uploadProgress[doc]}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 text-right mt-1">{uploadProgress[doc]}%</p>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => {
                              setActiveDocument(doc);
                              setShowCamera(true);
                            }}
                            className="bg-primary-50 text-primary-600 p-2 rounded-lg hover:bg-primary-100"
                          >
                            <SafeIcon icon={FiCamera} className="w-5 h-5" />
                          </button>
                          
                          <label className="bg-primary-600 text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-primary-700 flex items-center">
                            <SafeIcon icon={FiUpload} className="w-4 h-4 mr-1" />
                            <span className="text-sm">Upload</span>
                            <input
                              type="file"
                              accept="image/*, application/pdf"
                              className="hidden"
                              onChange={(e) => handleFileSelect(e, doc)}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    
                    {/* Document Preview */}
                    {documents[doc] && (
                      <div className="mt-4">
                        {documents[doc].type.startsWith('image/') ? (
                          <img 
                            src={documents[doc].url} 
                            alt={doc}
                            className="max-h-40 rounded-lg mx-auto"
                          />
                        ) : (
                          <div className="flex items-center justify-center bg-gray-100 p-4 rounded-lg">
                            <SafeIcon icon={FiFileText} className="w-8 h-8 text-gray-500 mr-2" />
                            <span>{documents[doc].name}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                ))}

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleContinue}
                    disabled={!allUploaded}
                    className="w-full sm:w-auto flex items-center justify-center"
                  >
                    Continue to Payment
                    <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Tips Sidebar */}
          <div>
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Upload Tips</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ensure all documents are clear and legible</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Use good lighting when taking photos of documents</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Avoid shadows or glare on documents</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ensure the entire document is visible in the frame</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>File size should be less than 5MB per document</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Supported formats: JPG, PNG, PDF</span>
                </li>
              </ul>

              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Privacy Guarantee</h4>
                    <p className="text-sm text-yellow-700">
                      Your documents are securely handled with our zero data sharing policy. All uploads are encrypted and never shared with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-4 max-w-lg w-full"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Capture {activeDocument}</h3>
              <button 
                onClick={() => setShowCamera(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="bg-black rounded-lg overflow-hidden">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full"
                mirrored={!useBackCamera}
                videoConstraints={{
                  facingMode: useBackCamera ? { ideal: 'environment' } : 'user'
                }}
              />
            </div>
            
            <div className="flex justify-center mt-4 gap-3">
              <Button
                variant="outline"
                onClick={() => setUseBackCamera(prev => !prev)}
              >
                {useBackCamera ? 'Use Front Camera' : 'Use Back Camera'}
              </Button>
              <Button
                onClick={captureFromWebcam}
                className="flex items-center"
              >
                <SafeIcon icon={FiCamera} className="w-5 h-5 mr-2" />
                Capture Photo
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              Make sure the document is clearly visible and well-lit
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;